<template>
  <Layout>
    <PageHeader :title="title" :items="items" />

    <div class="row" v-if="userData.permissions['view_loan_charges'] == 1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">

            <!-- Top Header & Search/Add Actions -->
            <div class="row mb-4 align-items-center">
              <div class="col-sm-12 col-md-6">
                <div class="d-flex align-items-center">
                  <label class="d-inline-flex align-items-center mb-0 me-3">
                    Show
                    <b-form-select class="form-select form-select-sm mx-2" v-model="perPage" size="sm"
                      :options="Options"></b-form-select>
                    entries
                  </label>
                  <div class="d-inline-flex align-items-center mx-3">
                    Search:
                    <b-form-input v-model="filter" type="search" placeholder="Search charges..."
                      class="form-control form-control-sm ms-2"></b-form-input>
                  </div>
                </div>
              </div>

              <div class="col-sm-12 col-md-6 text-md-end mt-3 mt-md-0">
                <b-button size="sm" variant="primary" @click="promptCreateCharge" v-if="List > 0 || isBusy">
                  <i class="mdi mdi-plus"></i> New Loan Charge
                </b-button>
              </div>

            </div>

            <!-- Table -->
            <div class="table-responsive">
              <b-table :items="Items" :fields="Fields" responsive="sm" :per-page="perPage" :current-page="currentPage"
                :filter="filter" :filter-included-fields="filterOn" @filtered="Filtered" :busy="IsBusy" show-empty
                class="align-middle table-nowrap table-hover">

                <!-- Loading State -->
                <template #table-busy>
                  <div class="text-center text-primary my-5">
                    <b-spinner class="align-middle" type="grow"></b-spinner>
                    <div class="mt-2 fw-medium">Loading Loan Charges...</div>
                  </div>
                </template>

                <!-- Empty State -->
                <template #empty>
                  <div class="text-center py-5">
                    <div class="mb-3">
                      <i class="mdi mdi-text-box-remove-outline text-muted" style="font-size: 64px;"></i>
                    </div>
                    <h5 class="text-dark">No Loan Charges Available</h5>
                    <p class="text-muted mb-4">You haven't added any loan charge ranges to the system yet.</p>
                    <b-button variant="primary" @click="promptCreateCharge" class="px-4">
                      <i class="mdi mdi-plus me-1"></i> Add Your First Loan Charge
                    </b-button>
                  </div>
                </template>

                <template #cell(index)="data">
                  {{ data.index + 1 }}
                </template>

                <template #cell(range_from)="data">
                  <span class="fw-bold">{{ commas(data.item.range_from) }}</span>
                </template>

                <template #cell(range_to)="data">
                  <span class="fw-bold">{{ commas(data.item.range_to) }}</span>
                </template>

                <template #cell(charge)="data">
                  <span v-if="data.item.charge">{{ commas(data.item.charge) }}</span>
                  <span v-else class="text-muted">-</span>
                </template>

                <template #cell(percentage)="data">
                  <span v-if="data.item.percentage">
                    <b-badge variant="soft-info" class="font-size-12">{{ data.item.percentage }}%</b-badge>
                  </span>
                  <span v-else class="text-muted">-</span>
                </template>

                <template #cell(status)="data">
                  <b-badge class="p-1 fw-bold"
                    :class="{ 'bg-primary': data.item.status == 'ACTIVE', 'bg-secondary': data.item.status == 'INACTIVE' }">
                    {{ data.item.status }}
                  </b-badge>
                </template>

                <template #cell(actions)="data">
                  <div class="d-flex gap-2">
                    <b-button title="Edit" size="sm" variant="info" @click="promptEditCharge(data.item)">
                      <i class="mdi mdi-pencil"></i>
                    </b-button>
                    <b-button v-if="data.item.status == 'INACTIVE'" title="Activate" size="sm" variant="primary"
                      @click="promptActivateCharge(data.item.id)"><i class="mdi mdi-check-bold"></i></b-button>
                    <b-button v-else title="Deactivate" size="sm" variant="secondary"
                      @click="promptDeactivateCharge(data.item.id)"><i class="mdi mdi-close"></i></b-button>
                    <b-button title="Delete" size="sm" variant="danger" @click="promptDeleteCharge(data.item.id)">
                      <i class="mdi mdi-trash-can"></i>
                    </b-button>
                  </div>
                </template>
              </b-table>
            </div>

            <!-- Pagination -->
            <div class="row align-items-center mt-3" v-if="List > 0">
              <div class="col-sm-12 col-md-6">
                <div class="text-muted">
                  Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, TotalRows) }} of
                  {{ TotalRows }} entries
                </div>
              </div>
              <div class="col-sm-12 col-md-6">
                <div class="dataTables_paginate paging_simple_numbers float-end">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="TotalRows" :per-page="perPage"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Permission Denied -->
    <div v-else>
      <div class="row justify-content-center">
        <div class="col-md-8 col-xl-6">
          <div class="text-center py-5 card card-body shadow-sm">
            <img src="@/assets/images/profile-img.png" alt="No Permission" class="img-fluid mx-auto d-block"
              style="max-width: 200px;" /><br>
            <h5 class="text-primary mt-3">Access Denied</h5>
            <span class="text-muted">You do not have the required permissions to view Loan Charges.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Charge Modal -->
    <b-modal id="create-charge" :title="editMode ? 'Edit Loan Charge' : 'Create Loan Charge Rule'" hide-footer centered>
      <form @submit.prevent="saveCharge">

        <!-- Global overlap error -->
        <div class="alert alert-danger p-2" v-if="formErrors.overlap">
          <i class="mdi mdi-alert-circle me-1"></i> {{ formErrors.overlap }}
        </div>

        <div class="row">
          <div class="col-md-6 form-group mb-4">
            <label class="form-label">Range From ({{ userData.currency }})</label>
            <input v-number-format type="text" inputmode="numeric" v-model="newCharge.range_from" class="form-control"
              :class="{ 'is-invalid': formErrors.range_from }" placeholder="e.g 0" @blur="validateRanges" required>
            <div class="invalid-feedback" v-if="formErrors.range_from">{{ formErrors.range_from }}</div>
          </div>
          <div class="col-md-6 form-group mb-4">
            <label class="form-label">Range To ({{ userData.currency }})</label>
            <input v-number-format type="text" inputmode="numeric" v-model="newCharge.range_to" class="form-control"
              :class="{ 'is-invalid': formErrors.range_to }" placeholder="e.g 1,000,000" @blur="validateRanges"
              required>
            <div class="invalid-feedback" v-if="formErrors.range_to">{{ formErrors.range_to }}</div>
          </div>
        </div>

        <hr class="mt-0 mb-4" />

        <!-- Modern Selection Design for Charge Type -->
        <div class="form-group mb-4">
          <label class="form-label d-block text-center mb-3">Select Charge Type</label>
          <div class="row g-2">
            <div class="col-6">
              <div class="card shadow-none border cursor-pointer h-100 mb-0 transition-all"
                :class="chargeType === 'FIXED' ? 'border-primary bg-soft-primary' : ''" @click="chargeType = 'FIXED'"
                style="cursor: pointer;">
                <div class="card-body text-center p-3">
                  <i class="mdi mdi-cash fs-2" :class="chargeType === 'FIXED' ? 'text-primary' : 'text-muted'"></i>
                  <h6 class="mt-2 mb-0" :class="chargeType === 'FIXED' ? 'text-primary' : 'text-muted'">Fixed Amount
                  </h6>
                </div>
              </div>
            </div>
            <div class="col-6">
              <div class="card shadow-none border cursor-pointer h-100 mb-0 transition-all"
                :class="chargeType === 'PERCENTAGE' ? 'border-primary bg-soft-primary' : ''"
                @click="chargeType = 'PERCENTAGE'" style="cursor: pointer;">
                <div class="card-body text-center p-3">
                  <i class="mdi mdi-percent fs-2"
                    :class="chargeType === 'PERCENTAGE' ? 'text-primary' : 'text-muted'"></i>
                  <h6 class="mt-2 mb-0" :class="chargeType === 'PERCENTAGE' ? 'text-primary' : 'text-muted'">Percentage
                  </h6>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group mb-4" v-if="chargeType === 'FIXED'">
          <label class="form-label">Fixed Charge Amount ({{ userData.currency }})</label>
          <input v-number-format type="text" inputmode="numeric" v-model="newCharge.charge" class="form-control"
            placeholder="e.g 50,000" required>
        </div>

        <div class="form-group mb-4" v-if="chargeType === 'PERCENTAGE'">
          <label class="form-label">Percentage (%)</label>
          <input type="number" v-model="newCharge.percentage" class="form-control" step="0.1" max="100" min="0.1"
            placeholder="e.g 2.5" required>
        </div>

        <div class="d-flex justify-content-end mt-2">
          <b-button type="button" variant="light" @click="$bvModal.hide('create-charge')"
            class="me-2 px-4">Cancel</b-button>
          <b-button type="submit" variant="primary" class="px-4" :disabled="hasErrors || isActionLoading">
            <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-content-save me-1"></i> {{ isActionLoading ? 'Saving...' : (editMode ? 'Update Charge' : 'Save Charge') }}
          </b-button>
        </div>
      </form>
    </b-modal>

    <!-- Confirmation Modals -->

    <!-- Activate Modal -->
    <b-modal id="activate-charge-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-primary mb-2">
          <i class="mdi mdi-check-circle-outline"></i>
        </div>
        <h4 class="text-primary mb-3">Activate Loan Charge?</h4>
        <p class="fs-6 text-muted mb-4">
          Are you sure you want to <strong>activate</strong> this loan charge? <br>
          Activating this rule makes it apply automatically to newly configured loan products falling in this tier.
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('activate-charge-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="primary" @click="executeActivateCharge" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-check-bold me-1"></i> {{ isActionLoading ? 'Activating...' : 'Yes, Activate' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Deactivate Modal -->
    <b-modal id="deactivate-charge-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-secondary mb-2">
          <i class="mdi mdi-close-circle-outline"></i>
        </div>
        <h4 class="text-secondary mb-3">Deactivate Loan Charge?</h4>
        <p class="fs-6 text-muted mb-4">
          Are you sure you want to <strong>deactivate</strong> this loan charge? <br>
          Deactivating this rule makes it unavailable for use on newly issued loans.
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('deactivate-charge-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="secondary" @click="executeDeactivateCharge" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-close me-1"></i> {{ isActionLoading ? 'Deactivating...' : 'Yes, Deactivate' }}
        </b-button>
      </div>
    </b-modal>

    <!-- Delete Modal -->
    <b-modal id="delete-charge-modal" hide-footer centered hide-header>
      <div class="text-center my-4">
        <div class="display-4 text-danger mb-2">
          <i class="mdi mdi-trash-can-outline"></i>
        </div>
        <h4 class="text-danger mb-3">Delete Loan Charge?</h4>
        <p class="fs-6 text-muted mb-4">
          This deletes the charge configuration permanently from the system. <br> Existing loans already processed with
          this
          fee will
          remain unaffected.
        </p>
      </div>
      <div class="d-flex justify-content-center mt-4">
        <b-button variant="light" @click="$bvModal.hide('delete-charge-modal')" class="me-3 px-4">Cancel</b-button>
        <b-button variant="danger" @click="executeDeleteCharge" class="px-4" :disabled="isActionLoading">
          <b-spinner small v-if="isActionLoading" class="me-1"></b-spinner><i v-else class="mdi mdi-trash-can me-1"></i> {{ isActionLoading ? 'Deleting...' : 'Delete' }}
        </b-button>
      </div>
    </b-modal>

  </Layout>
</template>

<script>
import axios from "axios";
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import appConfig from "@/app.config";
import { loanchargeComputed, loanchargeMethods } from "@/state/helpers";

export default {
  page: {
    title: "Loan Charges",
    meta: [{ name: "description", content: appConfig.description, },],
  },
  components: {
    Layout, PageHeader
  },
  directives: {
    numberFormat: {
      bind(el) {
        function handler() {
          if (el._formatting) return;
          const digits = el.value.replace(/\D/g, '');
          const formatted = digits ? parseInt(digits, 10).toLocaleString('en-US') : '';
          if (el.value !== formatted) {
            el._formatting = true;
            el.value = formatted;
            el.dispatchEvent(new Event('input', { bubbles: true }));
            el._formatting = false;
          }
        }
        el.addEventListener('input', handler);
        el._numberFormatHandler = handler;
      },
      // Re-run the formatter whenever the element is (re)inserted, or its
      // value is set programmatically (e.g. pre-filling the Edit form) —
      // only real keystrokes trigger the 'input' listener above, so without
      // this, prefilled values are shown unformatted.
      inserted(el) {
        el._numberFormatHandler();
      },
      componentUpdated(el) {
        el._numberFormatHandler();
      },
      unbind(el) {
        el.removeEventListener('input', el._numberFormatHandler);
      }
    }
  },
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")),
      title: "Loan Charges Configuration",
      items: [{ text: "Dashboard", to: "/", }, { text: "Loan Charges", active: true, },],
      TotalRows: 0,
      currentPage: 1,
      perPage: 10,
      Options: [10, 50, 100, 150, 200],
      filter: null,
      filterOn: [],
      isBusy: true,
      isActionLoading: false,
      editMode: false,
      selectedTargetId: null,
      chargeType: 'FIXED',
      formErrors: {
        range_from: '',
        range_to: '',
        overlap: ''
      },
      fields: [
        { key: 'index', label: '#', visible: true, class: 'align-middle' },
        { key: 'id', label: 'ID', visible: false },
        { key: 'range_from', label: 'Range From', visible: true, class: 'align-middle' },
        { key: 'range_to', label: 'Range To', visible: true, class: 'align-middle' },
        { key: 'charge', label: 'Charge Amount', visible: true, class: 'align-middle' },
        { key: 'percentage', label: 'Percentage', visible: true, class: 'align-middle' },
        { key: 'status', label: 'Status', visible: true, class: 'align-middle' },
        { key: 'actions', label: 'Actions', visible: true, class: 'align-middle text-end' },
      ],
      newCharge: {
        range_from: null,
        range_to: null,
        charge: null,
        percentage: null
      }
    };
  },
  computed: {
    ...loanchargeComputed,
    List() { return this.loancharges ? this.loancharges.length : 0; },
    Fields() { return this.fields.filter(field => field.visible) },
    Items() { return this.loancharges ? this.loancharges.map((d) => ({ ...d })) : [] },
    IsBusy() { return this.isBusy; },
    hasErrors() {
      return this.formErrors.range_from !== '' || this.formErrors.range_to !== '' || this.formErrors.overlap !== '';
    }
  },
  async mounted() {
    await this.fetchLoanCharges();
    this.TotalRows = this.List;
    this.isBusy = false;
  },
  watch: {
    List(newVal) {
      this.TotalRows = newVal;
    }
  },
  methods: {
    ...loanchargeMethods,
    commas(x) { return x ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : 0; },
    Filtered(filteredItems) {
      this.TotalRows = filteredItems.length;
      this.currentPage = 1;
    },

    // Resets form and opens create mode
    promptCreateCharge() {
      this.editMode = false;
      this.selectedTargetId = null;
      this.newCharge = { range_from: null, range_to: null, charge: null, percentage: null };
      this.chargeType = 'FIXED';
      this.formErrors = { range_from: '', range_to: '', overlap: '' };
      this.$bvModal.show('create-charge');
    },

    // Pre-fills data and opens edit mode
    promptEditCharge(item) {
      this.editMode = true;
      this.selectedTargetId = item.id;
      this.newCharge = {
        range_from: item.range_from,
        range_to: item.range_to,
        charge: item.charge,
        percentage: item.percentage
      };
      this.chargeType = item.percentage ? 'PERCENTAGE' : 'FIXED';
      this.formErrors = { range_from: '', range_to: '', overlap: '' };
      this.$bvModal.show('create-charge');
    },

    // Inline validation triggered on blur
    validateRanges() {
      this.formErrors = { range_from: '', range_to: '', overlap: '' };

      const rangeFrom = this.newCharge.range_from ? Number(String(this.newCharge.range_from).replace(/,/g, '')) : null;
      const rangeTo = this.newCharge.range_to ? Number(String(this.newCharge.range_to).replace(/,/g, '')) : null;

      if (rangeFrom !== null && rangeFrom < 0) {
        this.formErrors.range_from = 'The starting range must be 0 or greater.';
      }

      if (rangeFrom !== null && rangeTo !== null && rangeTo <= rangeFrom) {
        this.formErrors.range_to = 'The ending range must be strictly greater than the starting range.';
      }

      // Overlap validation loop
      if (rangeFrom !== null && rangeTo !== null && !this.formErrors.range_to) {
        const hasOverlap = this.Items.some(existingCharge => {
          // If in edit mode, ignore comparing against itself
          if (this.editMode && existingCharge.id === this.selectedTargetId) return false;

          // Check if lines intersect
          return rangeFrom <= existingCharge.range_to && rangeTo >= existingCharge.range_from;
        });

        if (hasOverlap) {
          this.formErrors.overlap = 'This range overlaps with an existing loan charge range.';
        }
      }
    },

    // Handles Create or Update submission
    async saveCharge() {
      this.validateRanges();
      if (this.hasErrors) return;

      const payload = {
        range_from: Number(String(this.newCharge.range_from).replace(/,/g, '')),
        range_to: Number(String(this.newCharge.range_to).replace(/,/g, '')),
        charge: null,
        percentage: null
      };

      if (this.chargeType === 'FIXED') {
        payload.charge = this.newCharge.charge ? Number(String(this.newCharge.charge).replace(/,/g, '')) : null;
      } else {
        payload.percentage = this.newCharge.percentage;
      }

      this.isActionLoading = true;
      try {
        if (this.editMode) {
          payload.id = this.selectedTargetId;
          await axios.post('/update-loan-charge', payload);
          this.$toast.success('Loan charge updated successfully!');
        } else {
          await axios.post('/add-loan-charge', payload);
          this.$toast.success('Loan charge configured successfully!');
        }

        this.$bvModal.hide('create-charge');
        this.isBusy = true;
        await this.fetchLoanCharges();
        this.isBusy = false;
      } catch (error) {
        // Backend fallback validation handling
        if (error.response && error.response.status === 422) {
          const errors = error.response.data.errors;
          if (errors.range_from) {
            this.formErrors.overlap = errors.range_from[0];
          } else if (errors.range_to) {
            this.formErrors.range_to = errors.range_to[0];
          }
        } else {
          this.$toast.error(`Failed to ${this.editMode ? 'update' : 'create'} loan charge.`);
        }
      } finally {
        this.isActionLoading = false;
      }
    },

    promptActivateCharge(id) {
      this.selectedTargetId = id;
      this.$bvModal.show('activate-charge-modal');
    },
    promptDeactivateCharge(id) {
      this.selectedTargetId = id;
      this.$bvModal.show('deactivate-charge-modal');
    },
    promptDeleteCharge(id) {
      this.selectedTargetId = id;
      this.$bvModal.show('delete-charge-modal');
    },

    async executeActivateCharge() {
      this.isActionLoading = true;
      try {
        const obj = { id: this.selectedTargetId };
        const response = await axios.post(`/activate-loan-charge`, obj);
        if (response.status == 200) {
          this.$toast.success('Loan charge activated successfully!');
          this.isBusy = true;
          await this.fetchLoanCharges();
          this.isBusy = false;
        }
      } catch (error) {
        this.$toast.error('Failed to activate the loan charge.');
      } finally {
        this.isActionLoading = false;
        this.$bvModal.hide('activate-charge-modal');
      }
    },

    async executeDeactivateCharge() {
      this.isActionLoading = true;
      try {
        const obj = { id: this.selectedTargetId };
        const response = await axios.post(`/deactivate-loan-charge`, obj);
        if (response.status == 200) {
          this.$toast.success('Loan charge deactivated successfully!');
          this.isBusy = true;
          await this.fetchLoanCharges();
          this.isBusy = false;
        }
      } catch (error) {
        this.$toast.error('Failed to deactivate the loan charge.');
      } finally {
        this.isActionLoading = false;
        this.$bvModal.hide('deactivate-charge-modal');
      }
    },

    async executeDeleteCharge() {
      this.isActionLoading = true;
      try {
        const obj = { id: this.selectedTargetId };
        const response = await axios.post(`/delete-loan-charge`, obj);
        if (response.status == 200) {
          this.$toast.success('Loan charge deleted successfully!');
          this.isBusy = true;
          await this.fetchLoanCharges();
          this.isBusy = false;
        }
      } catch (error) {
        this.$toast.error('Failed to delete the loan charge.');
      } finally {
        this.isActionLoading = false;
        this.$bvModal.hide('delete-charge-modal');
      }
    }
  },
};
</script>